import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';

import { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormSection from '../../../components/form-section';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { CreateLocationInputDto } from '../../../models/location/dto/create-location.dto';
import SportSelect from 'components/SportSelect';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateLocation } from '../hooks/useCreateLocation';

const COUNTRY_OPTIONS = [
    { label: 'France', value: 'France' },
    { label: 'Émirats arabes unis', value: 'Émirats arabes unis' },
];

const initialFormData: CreateLocationInputDto = {
    name: '',
    country: 'France',
    sportTypes: [],
};
export default function CreateLocationForm() {
    const { selectedOrganization } = useSelectedOrganization();
    const { mutateAsync: createLocation, isLoading: isCreating } = useCreateLocation(selectedOrganization?.id);
    const [formData, setFormData] = useState<CreateLocationInputDto>({ ...initialFormData });
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const closeModal = () => {
        onClose();
        setFormData({ ...initialFormData });
    };

    const handleInputChange = (key: keyof typeof formData, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    const handleSubmit = async () => {
        try {
            await createLocation(formData);
            onClose();
        } catch {
            toast({
                title: translate('common.error'),
                status: 'error',
                duration: 3000,
                position: 'top-right'
            });
        }
    };

    const isInvalid = !formData.sportTypes.length || !formData.name;

    const countryOptions = COUNTRY_OPTIONS.map((c) => ({
        ...c,
        label: c.value === 'France'
            ? translate('locations.country_france')
            : translate('locations.country_uae'),
    }));

    return (
        <>
            <Button colorScheme="brand" onClick={onOpen}>
                {translate('locations.add_new_location')}
            </Button>
            <FormModal
                isOpen={isOpen}
                onClose={closeModal}
                title={translate('locations.add_new_location')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isCreating}
                isSubmitDisabled={isInvalid}
                size="xl"
            >
                <FormStack density="section">
                    {/* ── Details ── */}
                    <FormSection label={translate('locations.details')}>
                        <FormControl isInvalid={!formData.sportTypes.length}>
                            <FormLabel>{translate('locations.sports')}</FormLabel>
                            <SportSelect
                                isInvalid={!formData.sportTypes.length}
                                value={formData.sportTypes}
                                onChange={(newValues: SportType[]) =>
                                    handleInputChange('sportTypes', newValues)
                                }
                            />
                            {!formData.sportTypes.length && (
                                <FormErrorMessage>
                                    {translate('locations.sports_cannot_be_empty')}
                                </FormErrorMessage>
                            )}
                        </FormControl>
                        <FormField
                            label={translate('locations.name')}
                            value={formData.name}
                            onChange={(value: string) => handleInputChange('name', value)}
                            error={!formData.name ? translate('locations.name_cannot_be_empty') : undefined}
                            isRequired
                        />
                        <FormField
                            type="select"
                            label={translate('locations.country')}
                            value={formData.country ?? 'France'}
                            onChange={(value) => handleInputChange('country', value)}
                            options={countryOptions}
                        />
                    </FormSection>

                    {/* ── Address ── */}
                    <FormSection label={translate('locations.address')} columns={{ base: 1, md: 2 }}>
                        <FormField
                            label={translate('locations.address')}
                            value={formData.address ?? ''}
                            onChange={(value: string) => handleInputChange('address', value)}
                        />
                        <FormField
                            label={translate('locations.city')}
                            value={formData.city ?? ''}
                            onChange={(value: string) => handleInputChange('city', value)}
                        />
                        <FormField
                            label={translate('locations.postal_code')}
                            value={formData.postalCode ?? ''}
                            onChange={(value: string) => handleInputChange('postalCode', value)}
                        />
                        <FormField
                            label={translate('locations.latitude')}
                            value={String(formData.latitude ?? '')}
                            onChange={(value: string) => handleInputChange('latitude', value ? parseFloat(value) : undefined)}
                            placeholder="48.8566"
                            inputProps={{ type: 'number' }}
                        />
                        <FormField
                            label={translate('locations.longitude')}
                            value={String(formData.longitude ?? '')}
                            onChange={(value: string) => handleInputChange('longitude', value ? parseFloat(value) : undefined)}
                            placeholder="2.3522"
                            inputProps={{ type: 'number' }}
                        />
                    </FormSection>
                </FormStack>
            </FormModal>
        </>
    );
}
